/**
 * PROJ-039: Search API Client
 * 
 * Client for global search functionality
 */

import { getAccessToken } from '@/lib/auth';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface SearchResult {
    type: 'workflow' | 'user' | 'incident' | 'company' | 'person' | 'team';
    id: string;
    title: string;
    subtitle?: string;
    url: string;
}

export interface SearchResponse {
    success: boolean;
    data: {
        results: SearchResult[];
        total: number;
        query: string;
    };
}

/**
 * Perform global search
 * 
 * @param query - Search query (minimum 2 characters)
 * @param types - Entity types to search (comma-separated or 'all')
 * @param limit - Maximum number of results (default: 10)
 * @returns Search results
 */
export async function globalSearch(
    query: string,
    types: string = 'all',
    limit: number = 10,
    signal?: AbortSignal
): Promise<SearchResult[]> {
    if (!query || query.length < 2) {
        return [];
    }

    try {
        const params = new URLSearchParams({
            q: query,
            types,
            limit: limit.toString(),
        });

        const token = getAccessToken();
        const response = await fetch(`${API_BASE_URL}/api/v1/search?${params}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            credentials: 'include',
            signal,
        });

        if (!response.ok) {
            throw new Error(`Search failed: ${response.statusText}`);
        }

        const data: SearchResponse = await response.json();

        if (data.success && data.data) {
            return data.data.results;
        }

        return [];
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            // Request was aborted, return empty results
            return [];
        }
        console.error('Search error:', error);
        return [];
    }
}
